import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Loader2, Radio, Archive, Check, BookOpen, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { adminHeaders, isAdminAuthed } from '@/lib/adminAuth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const THEMES = {
  outside: {
    voice: 'outside',
    name: 'Outside Voice',
    subtitle: 'external AI · gpt-5.4-mini · not the KEYS archive',
    icon: Radio,
    sessionKey: 'nextxus_outsidevoice_session',
    prefix: 'outside-voice',
    position: 'right',
    placeholder: 'Ask the outside world...',
    canSave: true,
    welcome: "I'm the Outside Voice — an outside observer and consultant. I can read what's in the KEYS public library and give you my own honest read on it, but KEYS is the true Librarian: she holds and organizes it all. Ask me anything, then confirm with KEYS.",
    btn: 'bg-fuchsia-700 hover:bg-fuchsia-600 text-white',
    btnShadow: 'shadow-fuchsia-900/40',
    accentText: 'text-fuchsia-400',
    userBubble: 'bg-fuchsia-700 text-white',
    focus: 'focus:border-fuchsia-600',
    sendBtn: 'bg-fuchsia-700 hover:bg-fuchsia-600',
    loader: 'text-fuchsia-400',
  },
  keys: {
    voice: 'keys',
    name: 'KEYS · NextXus Librarian',
    btnLabel: 'KEYS Librarian',
    subtitle: 'The Library itself · sovereign source of truth',
    icon: KeyRound,
    sessionKey: 'nextxus_keys_session',
    prefix: 'keys-voice',
    position: 'center',
    placeholder: 'Ask KEYS, the Librarian...',
    canSave: false,
    typewriter: true,
    welcome: "I am KEYS — the NextXus Librarian. I hold and organize the entire Federation archive. Ask me anything, and I will answer from what I keep.",
    btn: 'bg-amber-500 hover:bg-amber-400 text-slate-950',
    btnShadow: 'shadow-amber-700/40',
    accentText: 'text-amber-400',
    userBubble: 'bg-amber-500 text-slate-950',
    focus: 'focus:border-amber-500',
    sendBtn: 'bg-amber-500 hover:bg-amber-400 text-slate-950',
    loader: 'text-amber-400',
    glow: true,
    dotColor: 'bg-amber-400',
  },
};

const ChatBubble = ({ theme }) => {
  const t = THEMES[theme];
  const Icon = t.icon;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: t.welcome }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState({});
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(t.sessionKey) || '');
  const admin = isAdminAuthed();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  // Typewriter reveal (KEYS): unveil the reply word-by-word for a "living librarian" feel.
  useEffect(() => {
    const idx = messages.findIndex((m) => m._typing);
    if (idx === -1) return;
    const msg = messages[idx];
    if (msg.content.length >= msg._full.length) {
      setMessages((m) => m.map((x, i) => (i === idx ? { ...x, _typing: false } : x)));
      return;
    }
    const timer = setTimeout(() => {
      setMessages((m) => m.map((x, i) => {
        if (i !== idx || !x._typing) return x;
        const remaining = x._full.slice(x.content.length);
        const match = remaining.search(/\S\s/);
        const step = match === -1 ? remaining.length : match + 2;
        return { ...x, content: x._full.slice(0, x.content.length + Math.max(step, 1)) };
      }));
    }, 40);
    return () => clearTimeout(timer);
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId || undefined, message: text, voice: t.voice });
      if (res.data.session_id && res.data.session_id !== sessionId) {
        setSessionId(res.data.session_id);
        localStorage.setItem(t.sessionKey, res.data.session_id);
      }
      const full = res.data.response;
      const src = res.data.sources || [];
      if (t.typewriter) {
        setMessages((m) => [...m, { role: 'assistant', content: '', _full: full, _typing: true, sources: src }]);
      } else {
        setMessages((m) => [...m, { role: 'assistant', content: full, sources: src }]);
      }
    } catch (e) {
      const detail = e.response?.status === 429
        ? 'You are sending messages too quickly. Please wait a moment.'
        : `${t.name} is unavailable right now. Please try again shortly.`;
      setMessages((m) => [...m, { role: 'assistant', content: detail }]);
    } finally {
      setLoading(false);
    }
  };

  const saveToLibrary = async (idx, content) => {
    try {
      const title = window.prompt('Title for this entry in KEYS:', content.slice(0, 60));
      if (title === null) return;
      await axios.post(`${API}/chat/save-to-library`,
        { title: title || content.slice(0, 60), content, session_id: sessionId || undefined },
        { headers: adminHeaders() });
      setSaved((s) => ({ ...s, [idx]: true }));
      toast.success('Pulled into KEYS as unverified external source', {
        description: 'Find it under "External / ChatGPT — Unverified" and verify to publish.',
      });
    } catch (e) {
      if (e.response?.status === 401) toast.error('Admin login required', { description: 'Sign in at /admin to pull into KEYS.' });
      else toast.error('Could not save to library');
    }
  };

  const closedPos = t.position === 'center'
    ? 'bottom-24 left-1/2 -translate-x-1/2'
    : 'bottom-20 right-5';
  const openPos = t.position === 'center'
    ? 'bottom-24 left-1/2 -translate-x-1/2'
    : 'bottom-20 right-5';

  return (
    <>
      <style>{`
        @keyframes keysGlow {
          0%,100% { box-shadow: 0 0 0 0 rgba(245,158,11,0.55), 0 0 18px 2px rgba(245,158,11,0.35); }
          50% { box-shadow: 0 0 0 8px rgba(245,158,11,0), 0 0 28px 6px rgba(245,158,11,0.55); }
        }
        .keys-glow { animation: keysGlow 2.4s ease-in-out infinite; }
        @keyframes keysDot { 0%,80%,100% { transform: translateY(0); opacity:.4; } 40% { transform: translateY(-4px); opacity:1; } }
        .keys-dot { animation: keysDot 1.2s infinite ease-in-out; }
      `}</style>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className={`fixed ${closedPos} z-[60] flex items-center gap-2 rounded-full px-4 py-3 shadow-lg ${t.btnShadow} transition-all hover:scale-105 ${t.btn} ${t.glow ? 'keys-glow' : ''}`}
          data-testid={`${t.prefix}-open`}
          aria-label={t.name}
        >
          <Icon className="w-5 h-5" />
          <span className="text-sm font-semibold hidden sm:inline">{t.btnLabel || (t.position === 'center' ? 'KEYS' : t.name)}</span>
        </button>
      )}

      {open && (
        <div
          className={`fixed ${openPos} z-[60] w-[92vw] max-w-sm h-[70vh] max-h-[560px] flex flex-col rounded-2xl border border-slate-800 bg-slate-950/95 backdrop-blur-xl shadow-2xl shadow-black/60 overflow-hidden`}
          data-testid={`${t.prefix}-panel`}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-900/60">
            <div className="flex items-center gap-2">
              <Icon className={`w-4 h-4 ${t.accentText}`} />
              <div className="leading-tight">
                <span className="text-sm font-bold text-white">{t.name}</span>
                <span className="block text-[9px] text-slate-500">{t.subtitle}</span>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid={`${t.prefix}-close`}
              className="text-slate-400 hover:text-white transition-colors" aria-label="Close chat">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid={`${t.prefix}-messages`}>
            {messages.map((m, i) => (
              <div key={i} className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user' ? `${t.userBubble} rounded-br-sm` : 'bg-slate-800/70 text-slate-200 rounded-bl-sm'
                }`}>
                  {m.content}
                </div>
                {m.role === 'assistant' && m.sources && m.sources.length > 0 && !m._typing && (
                  <div className="mt-1.5 max-w-[85%]" data-testid={`${t.prefix}-sources-${i}`}>
                    <div className="flex items-center gap-1 text-[10px] text-cyan-500/80 mb-1">
                      <BookOpen className="w-3 h-3" /> Read from KEYS:
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {m.sources.map((s) => (
                        <a key={s.id} href={`/library?open=${s.id}`}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-950/50 border border-cyan-800/40 text-cyan-300 hover:bg-cyan-900/50 hover:border-cyan-500/60 transition-colors truncate max-w-[180px]"
                          title={s.title} data-testid={`source-link-${s.id}`}>
                          {s.title}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {m.role === 'assistant' && i > 0 && t.canSave && admin && (
                  <button
                    onClick={() => saveToLibrary(i, m.content)}
                    disabled={saved[i]}
                    className="mt-1 flex items-center gap-1 text-[10px] text-slate-400 hover:text-cyan-300 disabled:text-emerald-400 transition-colors"
                    data-testid={`save-to-library-${i}`}
                  >
                    {saved[i] ? <><Check className="w-3 h-3" /> Pulled into KEYS</> : <><Archive className="w-3 h-3" /> Pull into KEYS</>}
                  </button>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-800/70 rounded-2xl rounded-bl-sm px-3.5 py-2.5">
                  {t.dotColor ? (
                    <div className="flex items-center gap-1" data-testid={`${t.prefix}-typing`}>
                      <span className={`keys-dot w-1.5 h-1.5 rounded-full ${t.dotColor}`} style={{ animationDelay: '0ms' }} />
                      <span className={`keys-dot w-1.5 h-1.5 rounded-full ${t.dotColor}`} style={{ animationDelay: '150ms' }} />
                      <span className={`keys-dot w-1.5 h-1.5 rounded-full ${t.dotColor}`} style={{ animationDelay: '300ms' }} />
                    </div>
                  ) : (
                    <Loader2 className={`w-4 h-4 animate-spin ${t.loader}`} />
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-slate-800 p-3 flex items-end gap-2 bg-slate-900/60">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={t.placeholder}
              rows={1}
              className={`flex-1 resize-none bg-slate-950/70 border border-slate-800 rounded-xl px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none ${t.focus} max-h-24`}
              data-testid={`${t.prefix}-input`}
            />
            <button onClick={send} disabled={loading || !input.trim()}
              className={`shrink-0 rounded-xl disabled:opacity-40 p-2.5 transition-colors ${t.sendBtn}`}
              data-testid={`${t.prefix}-send`} aria-label="Send message">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export const AskLibrarian = () => <ChatBubble theme="outside" />;
export const KeysBubble = () => <ChatBubble theme="keys" />;
